import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { fetchNiezgodneCentra } from '@/lib/cost-centers-api';
import { useI18n } from '@/lib/i18n';

/**
 * KTO MA NIEZGODNE CENTRUM KOSZTOWE — lista, po której da się przejść.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZONE 01.09.2026. Alarm mówił „popraw je przypisaniem w tabeli poniżej", a tabela na tym
 *  ekranie jest listą CENTRÓW, nie ludzi — więc dawał liczbę i nie dawał sposobu, żeby tych ludzi
 *  znaleźć. Trzeba było przejść całą listę pracowników i sprawdzać po jednym.
 *
 *  ⚠️ POKAZUJEMY OBIE POSTACIE OBOK SIEBIE, bo dopiero różnica między nimi jest tu informacją:
 *  napis przy pracowniku kontra centra, do których naprawdę należy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function NiezgodneCentra({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const { t } = useI18n();

  const dane = useQuery({
    queryKey: ['centra-niezgodni', groupId],
    queryFn: () => fetchNiezgodneCentra(groupId),
  });

  const rows = dane.data?.rows ?? [];
  const wszystkich = dane.data?.wszystkich ?? 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={t('cc.niezgodni.tytul')}
        description={t('cc.niezgodni.podtytul').replace('{n}', String(wszystkich))}
      >
        {dane.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        ) : rows.length === 0 ?
          /** ⚠️ Pusto po odświeżeniu znaczy „już poprawione" — i tak trzeba to napisać. */
          <p className="text-sm text-muted-foreground">{t('cc.niezgodni.pusto')}</p>
         : (
          <div className="max-h-[60vh] overflow-y-auto rounded border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/70 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="p-2">{t('cc.niezgodni.karta')}</th>
                  <th className="p-2">{t('cc.niezgodni.pracownik')}</th>
                  <th className="p-2">{t('cc.niezgodni.napis')}</th>
                  <th className="p-2">{t('cc.niezgodni.przynaleznosc')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="p-2 font-mono text-[11px]">{r.karta}</td>
                    <td className="p-2">
                      {/* ⚠️ Odnośnik do karty pracownika — poprawia się to u niego, nie tutaj. */}
                      <Link to={`/pracownicy/${r.id}`} className="underline-offset-4 hover:underline">
                        {r.name.trim() === '' ? <span className="italic text-muted-foreground">{t('emp.bezNazwy')}</span> : r.name}
                      </Link>
                      {r.wielu && (
                        <Badge variant="warning" className="ml-2">
                          {t('cc.niezgodni.wielu')}
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 font-mono text-[11px]">{r.napis ?? '—'}</td>
                    <td className="p-2 font-mono text-[11px]">
                      {r.przynaleznosc.length === 0 ? '—' : r.przynaleznosc.join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {wszystkich > rows.length && (
              /** ⚠️ Mówimy, że lista jest UCIĘTA — reszta nie zniknęła, tylko się nie zmieściła. */
              <p className="p-2 text-[11px] text-muted-foreground">
                {t('cc.niezgodni.uciete').replace('{n}', String(wszystkich - rows.length))}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
